import { Injectable } from '@angular/core';
import { IUserAssignments } from 'src/app/interfaces/self-serve/assignment-manager/iuser-assignments';
import {
  IParsedTeam,
  IParsedUser,
} from 'src/app/interfaces/self-serve/assignment-manager/iteams-with-users-parser';
import {
  IInputChildTeam,
  IInputTeam,
  IInputUser,
  IUsersWithTeams,
} from 'src/app/interfaces/self-serve/assignment-manager/responses/iusers-with-teams';

@Injectable({
  providedIn: 'root',
})
export class TeamUserParserService {
  unassignedTeamUuid = 'unassigned';
  unassignedUsersTeamName =
    'Users without teams (Must be part of a team before being added to assignment)';

  transformTeamUserData(data: IUsersWithTeams): IParsedTeam[] {
    const userMap = this.groupUsersByTeam(data.users);
    const teamsByUuid = new Map(
      data.teams.map((team) => [team.teamUuid, team])
    );

    // A team is a root only if no other team lists it as a child.
    const childUuids = new Set<string>();
    for (const team of data.teams) {
      for (const child of team.childTeams ?? []) {
        childUuids.add(child.uuid);
      }
    }
    const rootTeams = data.teams.filter(
      (team) => !childUuids.has(team.teamUuid)
    );

    const parsedTeams = rootTeams.map((team) =>
      this.buildTeamRecursive(team, teamsByUuid, userMap)
    );

    // Add unassigned users if any
    if (userMap[this.unassignedTeamUuid]?.length) {
      // Disable all unassigned users
      const disabledUnassignedUsers = userMap[this.unassignedTeamUuid].map(
        (user) => ({
          ...user,
          disabled: true,
        })
      );

      parsedTeams.push({
        uuid: this.unassignedTeamUuid,
        name: this.unassignedUsersTeamName,
        expanded: false,
        users: disabledUnassignedUsers,
        children: [],
        tags: [],
      });
    }

    return parsedTeams;
  }

  private groupUsersByTeam(users: IInputUser[]): Record<string, IParsedUser[]> {
    const map: Record<string, IParsedUser[]> = {};

    for (const user of users) {
      const teamId = user.teamUuid ?? this.unassignedTeamUuid;
      const teamName = user.teamName ?? this.unassignedUsersTeamName;

      const parsedUser: IParsedUser = {
        uuid: user.userUuid,
        teamUuid: teamId,
        teamName: teamName,
        name: `${user.firstName} ${user.lastName}`,
        email: user.email,
        selected: false,
        disabled: false,
      };

      if (!map[teamId]) {
        map[teamId] = [];
      }

      map[teamId].push(parsedUser);
    }

    return map;
  }

  private buildTeamRecursive(
    team: IInputTeam,
    teamsByUuid: Map<string, IInputTeam>,
    userMap: Record<string, IParsedUser[]>
  ): IParsedTeam {
    const children: IParsedTeam[] = (team.childTeams ?? []).map((child) => {
      const actualChildTeam = teamsByUuid.get(child.uuid);
      return actualChildTeam
        ? this.buildTeamRecursive(actualChildTeam, teamsByUuid, userMap)
        : this.buildTeamFromChildRef(child, userMap);
    });

    return {
      uuid: team.teamUuid,
      name: team.teamName,
      expanded: false,
      users: userMap[team.teamUuid] ?? [],
      children,
      tags: (team.tags ?? []).map((tag) => tag.name),
    };
  }

  // Fallback for a child reference that has no matching full team entry in
  // the flat teams list — builds directly from its own nested childTeams.
  private buildTeamFromChildRef(
    child: IInputChildTeam,
    userMap: Record<string, IParsedUser[]>
  ): IParsedTeam {
    const children: IParsedTeam[] = (child.childTeams ?? []).map(
      (grandchild) => this.buildTeamFromChildRef(grandchild, userMap)
    );

    return {
      uuid: child.uuid,
      name: child.name,
      expanded: false,
      users: userMap[child.uuid] ?? [],
      children,
      tags: [],
    };
  }

  markAssignedUsersOnParsedTeams(
    parsedTeams: IParsedTeam[],
    currentAssignees: IUserAssignments[]
  ): IParsedTeam[] {
    if (currentAssignees.length === 0) return parsedTeams;

    const assignmentSet = new Set(
      currentAssignees.map((a) => `${a.userUuid}__${a.teamUuid}`)
    );

    const markRecursive = (teams: IParsedTeam[]): IParsedTeam[] => {
      return teams.map((team) => {
        const updatedUsers = team.users.map((user) => {
          const isAssigned = assignmentSet.has(
            `${user.uuid}__${user.teamUuid}`
          );
          // Disable users if they are in unassigned team or if they are already assigned
          const shouldDisable =
            isAssigned || user.teamUuid === this.unassignedTeamUuid;
          return {
            ...user,
            selected: isAssigned,
            disabled: shouldDisable,
          };
        });

        const updatedChildren = markRecursive(team.children || []);
        return {
          ...team,
          users: updatedUsers,
          children: updatedChildren,
        };
      });
    };

    return markRecursive(parsedTeams);
  }
}
